import {Injectable}               from '@angular/core';
import {map}                      from 'rxjs/operators';
import {ObjectFactory}            from 'ts-tooling';
import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                                 from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {nameOf}                   from '../../../ap-core/utils/ap-name-of';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                                 from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {MapViewMode}              from '../../../ap-interface';
import {
  ApDynGridGroupColumnConfig
}                                 from '../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {MapViewStore}             from '../../../stores/layout/mapview.store';
import {
  GetRoundNumericService
}                                 from '../../../ap-utils/service/get-round-numeric.service';
import {GetRoundNumericPipe}      from '../../../ap-utils';
import {DriverStore}              from '../../../stores/docu/driver.store';
import {MachineStore}             from '../../../stores/docu/machine.store';
import {InstrumentStore}          from '../../../stores/docu/instrument.store';
import {ApDateService}            from '../../../ap-core/services/ap-date-service';
import {ApTranslationService}     from '../../../ap-utils/service/ap-translation.service';
import {FieldStore}               from '../../../stores/farm/field.store';
import {FormatType}               from '../../../ap-utils/types/ap-format-duration.types';
import {TimeFormatPipe}           from '../../../ap-utils/pipes/ap-format-duration.pipe';
import {TimeAreaService}          from '../../../services/data/time-area.service';
import {FwEntryComponentsService} from '../services/fw-entry-components.service';
import {IFwPlanShowGridItem}      from './fw-plan-show.types';
import {
  ApTooltipContentService
}                                 from '../../../ap-utils/service/ap-tooltip-content.service';
import IGuid = System.IGuid;
import ILanguage = Data.Language.ILanguage;
import ISettings = Data.BaseData.ISettings;
import IFieldWorkPlanBook = Data.TaskManagement.IFieldWorkPlanBook;

@Injectable()
export class FwPlanShowGridConfig {
  constructor(private fieldStore: FieldStore,
              private driverStore: DriverStore,
              private mapViewStore: MapViewStore,
              private machineStore: MachineStore,
              private instrumentStore: InstrumentStore,
              private dateService: ApDateService,
              private timeAreaService: TimeAreaService,
              private translationService: ApTranslationService,
              private roundNumericService: GetRoundNumericService,
              private tooltipContentService: ApTooltipContentService,
              private fwEntryComponentService: FwEntryComponentsService,
              private timeFormatPipe: TimeFormatPipe,
              private roundNumericPipe: GetRoundNumericPipe) {
  }

  /**
   * Converts plan books to grid items by mapping required properties and resolving related data
   * @param planBooks The plan books to convert
   * @param plansToShow IDs of plans to include
   * @param isBookedItems Whether to include only booked or unbooked items
   * @returns Array of grid items with mapped properties
   * @public
   */
  public getGridItems(planBooks: IFieldWorkPlanBook[], plansToShow: IGuid[], isBookedItems: boolean): IFwPlanShowGridItem[] {
    return planBooks.filter(x => plansToShow.includes(x.Id))
      .filter(x => isBookedItems ? !!x.BookedAt : !x.BookedAt)
      .map(planBook => {
        const driver = this.driverStore.getDriverById(planBook.ExportDriver);
        const instrument = this.instrumentStore.getInstrumentById(planBook.ExportInstrument);
        const machine = this.machineStore.getMachineById(planBook.ExportMachine);
        const field = this.fieldStore.getFieldById(planBook.FieldId);
        const fieldGeom = this.fieldStore.getCurrentFieldGeom(field);
        const start = this.fwEntryComponentService.getTimeSpanInDate(planBook.Start);
        const haPerHour = this.timeAreaService.getHaPerHoursFromTotalMilliseconds(fieldGeom?.AreaHa,
          planBook.Duration?.TotalMilliseconds);
        const gridItem: IFwPlanShowGridItem = {
          Id: planBook.Id,
          FieldId: field?.Id,
          FieldName: field?.FieldName,
          FieldNumber: field?.FieldNumber,
          FieldSecondNumber: field?.FieldSubnumber,
          FieldCombinedNumber: this.fieldStore.getFieldNumberAndSubNumber(field),
          ApplicationDate: this.dateService.getDateMidnight(planBook.ApplicationDate),
          Area: fieldGeom?.AreaHa ?? 0,
          Start: start,
          DurationInMilliseconds: planBook.Duration?.TotalMilliseconds,
          HaPerHour: haPerHour,
          Value: planBook.Value,
          Unit: this.translationService.translate(planBook.Unit?.Key),
          WorkType: this.translationService.translate(planBook.WorkType?.Description),
          DriverName: driver?.FirstNameFullName,
          MachineName: machine?.NameWithSign,
          InstrumentName: instrument?.Name,
          PlanBookCopy: ObjectFactory.Copy(planBook)
        };
        return gridItem;
      });
  }

  /**
   * Gets grid column configurations based on language and settings
   * @param language The current language
   * @param setting The current settings
   * @returns Array of column configurations
   * @public
   */
  public getGridColumns(language: ILanguage, setting: ISettings): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanShowGridItem>('FieldNumber'),
        title: 'Docu_Ini__Number',
        width: 60,
        cssClass: 'right',
        hide: true,
        filterable: true,
        filterType: FilterType.NUMBER,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanShowGridItem>('FieldSecondNumber'),
        title: 'Docu_Ini_Subnumber',
        width: 60,
        cssClass: 'right',
        hide: true,
        filterable: true,
        filterType: FilterType.NUMBER,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanShowGridItem>('FieldCombinedNumber'),
        title: 'Docu_Ini__Number',
        width: 70,
        hide: new ApDynGridColumnHideConfig({
          selfHide: this.mapViewStore.Listen(s => s.mode).pipe(map((mode) => mode === MapViewMode.HIDE))
        }),
        filterable: true,
        headerFilterable: true,
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanShowGridItem>('FieldName'),
        title: 'Docu_Ini__Name',
        width: 180,
        sortIndex: 0,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanShowGridItem>('Area'),
        title: 'Docu_Ini__Area',
        width: 60,
        cssClass: 'right',
        tooltip: (dataItem: IFwPlanShowGridItem) => this.roundNumericService.stringify(dataItem?.Area, 4),
        filterable: true,
        filterType: FilterType.NUMBER,
        headerFilterable: true,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [setting.DigitsAfterDecimalPoint]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanShowGridItem>('ApplicationDate'),
        title: 'Base__SortItemDate',
        format: language.DateFormat,
        hide: true,
        width: 70,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanShowGridItem>('Value'),
        title: 'Global_Value',
        hide: true,
        width: 70,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanShowGridItem>('Unit'),
        title: 'Settings__Lbl_Menu_Unit',
        hide: true,
        width: 70,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanShowGridItem>('Start'),
        title: 'Docu_Ini__StartTime',
        format: 'HH:mm',
        hide: true,
        width: 70,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanShowGridItem>('DurationInMilliseconds'),
        title: `${this.translationService.translate('TotalTime')} [${this.translationService.translate('Base__UnitH')}]`,
        hide: true,
        width: 70,
        cssClass: 'right',
        headerFilterable: true,
        tooltip: (dataItem: IFwPlanShowGridItem) => this.tooltipContentService.getCapacityPerformanceTooltip(dataItem.HaPerHour),
        pipes: [{
          pipe: this.timeFormatPipe,
          args: [{
            formatType: FormatType.Decimals,
            suffixValue: 'Base__UnitH',
            decimalsFormatOptions: {
              decimalsCount: 1,
              decimalsSeparator: language.DecimalSeperator
            }
          }]
        }]
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Settings__Lbl_Menu_Resources',
        hide: true,
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IFwPlanShowGridItem>('MachineName'),
            title: 'Base__Engine',
            width: 100,
            hide: true,
            headerFilterable: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IFwPlanShowGridItem>('InstrumentName'),
            title: 'AgriConnect_Ini__Tool',
            width: 100,
            hide: true,
            headerFilterable: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IFwPlanShowGridItem>('DriverName'),
            title: 'Docu_Ini__Motorist',
            width: 100,
            hide: true,
            headerFilterable: true
          })
        ]
      })
    ];
  }
}
